<template>
  <NotificationInPage
    :class="$style.notificationContainer"
    :data-test-id="$testId('existingActivationInfoBanner')"
    :notification="notification"
    :showNotification="showNotification"
  />
</template>

<script>

import {
  ModuleConsts,
} from 'client/util/Constants';
import {
  NotificationInPage,
  Types,
} from '@jumpcloud/ui-components';
import { mapGetters, mapState } from 'vuex';
import {
  userStateActionMap,
  userStateActionType,
} from 'client/systemusers/constants/SystemUserConstants';
import DateUtil from 'client/util/DateUtil';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import pluralize from 'pluralize';

const { NotificationConfig } = Types;
const { activated, suspended } = ModuleConsts.systemUser.accountStates;

const systemUserCollection = 'SystemUserCollection';
const systemUserModel = 'SystemUserModel';

export default {
  name: 'ScheduleNotification',

  components: {
    NotificationInPage,
  },

  props: {
    inAsidePanel: Boolean,

    notificationAction: {
      type: String,
      default: undefined,
    },
  },

  dateFormat: 'MM/DD/YYYY [at] hh:mm A',

  data() {
    return {
      showNotification: false,
    };
  },

  computed: {
    ...mapGetters(systemUserCollection, {
      getDisplayName: 'getDisplayName',
      selectedItems: 'getSelectedItems',
    }),

    ...mapState(systemUserCollection, {
      scheduledStateEventsByOrg: 'scheduledStateChanges',
    }),

    ...mapState(systemUserModel, {
      item: 'item',
      scheduledStateEventsByUser: 'scheduledStateChangesForUser',
    }),

    action() {
      return this.notificationAction;
    },

    notification() {
      const config = NotificationConfig({
        icon: 'info',
        title: '',
        type: 'informational',
      });
      const count = this.scheduledEventCount;

      if (count > 0) {
        const { noun } = userStateActionMap.get(this.action);

        if (this.inAsidePanel) {
          const state = this.action === userStateActionType.activate ? activated : suspended;
          const firstEvent = this.scheduledStateEventsByUser.find(event => event.state === state);
          const scheduledDate = DateUtil.customFormatDate(
            firstEvent.scheduledDate,
            this.$options.dateFormat,
          );

          config.title = `${this.getDisplayName(this.item)} already has a scheduled ${noun} date set for ${scheduledDate}.`;
        } else {
          const verb = count > 1 ? 'have' : 'has a';

          config.title = `${this.scheduledEventCount} selected ${pluralize('user', count)} already ${verb} scheduled ${pluralize(noun, count)}.`;
        }
      }

      return config;
    },

    /**
     * If we're on the Users page, determine how many of the selected users have upcoming events.
     *
     * If we're on the aside, see if the selected user has any upcoming events; we can't rely on
     * selectedItems for the aside because the admin doesn't have to select the user before opening
     * the aside.
     */
    scheduledEventCount() {
      let count = 0;
      const state = this.action === userStateActionType.activate ? activated : suspended;

      if (this.inAsidePanel) {
        count = this.scheduledStateEventsByUser
          .filter(event => event.state === state)
          .length;
      } else {
        this.selectedItems.forEach((id) => {
          const found = this.scheduledStateEventsByOrg
            .find(event => event.systemUserId === id && event.state === state);

          if (found !== undefined) {
            count += 1;
          }
        });
      }

      return count;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    handleMounted() {
      this.showNotification = this.scheduledEventCount > 0;
    },
  },

  kbLink: KbArticleUrls.managingUserStates,

};
</script>

<style module scoped>
.learnMore {
  padding-left: var(--jc-spacer-x-small);
}

.notificationContainer {
  margin-bottom: var(--jc-spacer);
}
</style>
